import {HttpErrorResponse} from '@angular/common/http';
import {IAuthRequest} from '@app/auth-config/models/auth-request.model';
import {IAuthResponse} from '@app/auth-config/models/auth-response.model';
import {ELoginStatus} from '@app/auth-config/models/auth-state.model';
import {AuthConfigService} from '@app/auth-config/services/auth-config.service';
import {authenticateFailure, authenticateSuccess, preAuthForLanguageLoader} from '@app/auth-config/state/auth.actions';
import {selectTokenStatus} from '@app/auth-config/state/auth.selector';
import {TokenUtils} from '@app/auth-config/utils/token.utils';
import {LanguageService} from '@app/shared/services/language.service';
import {environment} from '@environments/environment';
import {Store} from '@ngrx/store';
import {TranslateLoader} from '@ngx-translate/core';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {catchError, EMPTY, iif, Observable, retry, switchMap, tap} from 'rxjs';
import {StorageKeys} from '../constants/storage-keys';
import {PlatformStorage} from '../tokens/platform-storage.token';

export class LanguageLoader implements TranslateLoader {
  static readonly MAX_AUTH_RETRIES = 2;
  loginStatus: ELoginStatus = ELoginStatus.Initial;

  constructor(
    private readonly languageService: LanguageService,
    private readonly store: Store,
    private readonly authConfigService: AuthConfigService,
    private readonly storage: PlatformStorage,
    public readonly oidcSecurityService: OidcSecurityService
  ) {
    this.store.dispatch(preAuthForLanguageLoader());
    this.store
      .select(selectTokenStatus)
      .pipe(
        tap(state => {
          this.loginStatus = state;
        })
      )
      .subscribe();
  }

  getTranslation(languageId: string): Observable<{[key: string]: string}> {
    return this.checkAuthAndInitLanguageService(languageId);
  }

  private checkAuthAndInitLanguageService(languageId: string) {
    return this.oidcSecurityService.getAccessToken()?.pipe(
      switchMap(token => {
        return iif(
          () => TokenUtils.isTokenValid(token),
          this.languageService.getLocalizeProperties(languageId),
          this.getPreAuthAndCallLanguageService(languageId)
        );
      })
    );
  }

  private getPreAuthAndCallLanguageService(languageId: string) {
    return this.authConfigService.getPreAuth(this.getAuthRequest(environment.oidcConfig.authRequest)).pipe(
      tap((response: IAuthResponse) => {
        this.storage.setItem(StorageKeys.accessToken, response.access_token);
        this.store.dispatch(
          authenticateSuccess({
            token: response.access_token,
            status: ELoginStatus.Authorized,
          })
        );

        this.store.dispatch(authenticateFailure());
      }),
      switchMap(() => this.languageService.getLocalizeProperties(languageId)),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.store.dispatch(authenticateFailure());
        return EMPTY;
      }),
      retry(LanguageLoader.MAX_AUTH_RETRIES)
    );
  }

  private getAuthRequest(requestParameter: string): IAuthRequest {
    const [clientId, grantType, clientSecret, scope] = requestParameter.split(',');
    const request: IAuthRequest = {
      client_id: clientId,
      client_secret: grantType,
      grant_type: clientSecret,
      scope: scope,
    };

    return request;
  }
}
